import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, SectionHeader } from "../components/Card";

/**
 * TechniciansPage - Public directory of active technicians
 *
 * Features:
 * - Grid layout of technicians with portfolio_visible = true
 * - Profile pictures
 * - Bio snippets
 * - Average ratings with review counts
 * - Specialties tags
 * - Click to view detailed profile
 */
export function TechniciansPage() {
  const navigate = useNavigate();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadTechnicians();
  }, []);

  async function loadTechnicians() {
    try {
      const response = await fetch("/api/public/technicians");
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setTechnicians(data.technicians || []);
    } catch (err) {
      setError(err.message || "Failed to load technicians");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader
          emoji="💅"
          title="Our Technicians"
          subtitle="Meet our talented nail technicians. View portfolios, read reviews, and book with your favorite."
        />

        {loading && (
          <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
            <div>Loading technicians...</div>
          </div>
        )}

        {error && (
          <Card style={{ maxWidth: "100%", margin: 0 }}>
            <div style={{ textAlign: "center", color: "#C02020" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
              <div>{error}</div>
            </div>
          </Card>
        )}

        {!loading && !error && technicians.length === 0 && (
          <Card style={{ maxWidth: "100%", margin: 0 }}>
            <div style={{ textAlign: "center", color: "var(--text-muted)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💅</div>
              <div>No technicians available at the moment.</div>
            </div>
          </Card>
        )}

        {!loading && !error && technicians.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))",
            gap: 20
          }}>
            {technicians.map((tech) => {
              const avgRating = tech.avg_rating || 0;
              const reviewCount = tech.review_count || 0;

              return (
                <Card
                  key={tech.id}
                  style={{
                    padding: 0,
                    overflow: "hidden",
                    maxWidth: "none",
                    margin: 0,
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => navigate(`/technicians/${tech.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                  }}
                >
                  {/* Avatar Section */}
                  <div style={{
                    height: 200,
                    background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden"
                  }}>
                    {tech.avatar_url ? (
                      <img
                        src={tech.avatar_url}
                        alt={tech.full_name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    ) : (
                      <div style={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        border: "4px solid rgba(255,255,255,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 48
                      }}>
                        {(tech.full_name || "?")[0].toUpperCase()}
                      </div>
                    )}

                    {/* Rating Badge */}
                    {avgRating > 0 && (
                      <div style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "rgba(255,255,255,0.95)",
                        padding: "6px 12px",
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#FFB800",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                      }}>
                        ⭐ {avgRating.toFixed(1)}
                        <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
                          ({reviewCount})
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info Section */}
                  <div style={{ padding: 16 }}>
                    <h3 style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 20,
                      fontWeight: 700,
                      color: "var(--text)",
                      marginBottom: 8,
                      marginTop: 0
                    }}>
                      {tech.full_name}
                    </h3>

                    {tech.bio && (
                      <p style={{
                        fontSize: 14,
                        color: "var(--text-muted)",
                        lineHeight: 1.5,
                        marginBottom: 12,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}>
                        {tech.bio}
                      </p>
                    )}

                    {/* Specialties */}
                    {tech.specialties && tech.specialties.length > 0 && (
                      <div style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginTop: 12
                      }}>
                        {tech.specialties.slice(0, 4).map((spec, i) => (
                          <span key={i} style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: 999,
                            background: "rgba(196,135,154,0.12)",
                            color: "var(--mauve)"
                          }}>
                            {spec}
                          </span>
                        ))}
                        {tech.specialties.length > 4 && (
                          <span style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: 999,
                            background: "var(--bg-soft)",
                            color: "var(--text-muted)"
                          }}>
                            +{tech.specialties.length - 4} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* View Profile Link */}
                    <div style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: "1px solid var(--border)",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--mauve)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}>
                      <span>View Portfolio</span>
                      <span>→</span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
